import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { 
  Account, 
  Invoice, 
  User, 
  ChangelogEntry, 
  AccountNote, 
  AccountSummary, 
  AuthResponse,
  ToolboxConfig,
  PdfTemplate,
  SetTemplateRequest,
  SyncResult
} from '../types';
import { 
  OnboardingUserProgress, 
  OnboardingSession, 
  OnboardingSessionCreateDto, 
  OnboardingSessionUpdateDto,
  OnboardingTemplate,
  AddUserWithTemplateRequest,
  PreviewTemplateScheduleRequest,
  PreviewSession,
  DatabasePreviewSession,
  PreviewDatabaseInsertRequest
} from '../types/onboarding.types';

// API service using real API endpoints
class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Create axios instance with static API URL
    // https://tapi.cstapi.co.uk/api  or http://localhost:5001/api
    const apiUrl = 'https://tapi.cstapi.co.uk/api';
    
    console.log('API URL:', apiUrl); // Debug log
    
    this.api = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Add response interceptor to handle 401 errors globally
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          console.log('Global 401 interceptor: clearing token and redirecting to login');
          this.clearToken();
          // Optionally trigger a global event or redirect to login
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return Promise.reject(error);
      }
    );

    // Load token from localStorage if available
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      this.setToken(savedToken);
    }
  }

  // Set auth token
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
  }

  // Clear auth token
  clearToken(): void {
    this.token = null;
    localStorage.removeItem('token');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.token !== null;
  }

  // Authentication
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', { username, password });
    if (response.data.token) {
      this.setToken(response.data.token);
    }
    return response.data;
  }

  async validateToken(token: string): Promise<boolean> {
    try {
      // Set the token for this request
      this.setToken(token);
      
      const response = await this.api.post('/auth/validate', { token });
      return response.status === 200;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      // Check if we have a token
      if (!this.token) {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          this.setToken(storedToken);
        } else {
          console.error('No token available for getCurrentUser request');
          return null;
        }
      }
      
      const response = await this.api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      // If we get a 401 Unauthorized, clear the token
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        console.log('Unauthorized response, clearing token');
        this.clearToken();
      }
      return null;
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  // Update profile
  async updateProfile(firstName: string, lastName: string, email: string): Promise<{ message: string }> {
    const response = await this.api.put<{ message: string }>('/auth/profile', {
      firstName,
      lastName,
      email
    });
    return response.data;
  }

  // Credit Control Accounts
  async getAccounts(): Promise<Account[]> {
    const response = await this.api.get<Account[]>('/credit-control/accounts');
    return response.data;
  }

  async getAccountById(contactID: string): Promise<Account | undefined> {
    try {
      const response = await this.api.get<Account>(`/credit-control/accounts/${contactID}`);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async createAccount(account: Omit<Account, 'id'>): Promise<Account> {
    const response = await this.api.post<Account>('/credit-control/accounts', account);
    return response.data;
  }

  async updateAccount(contactID: string, account: Partial<Account>): Promise<Account | undefined> {
    try {
      const response = await this.api.put<Account>(`/credit-control/accounts/${contactID}`, account);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async updateAccountById(id: number, account: Partial<Account>): Promise<Account | undefined> {
    try {
      const response = await this.api.put<Account>(`/credit-control/accounts/byid/${id}`, account);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async deleteAccount(contactID: string): Promise<boolean> {
    try {
      await this.api.delete(`/credit-control/accounts/${contactID}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Invoices
  async getInvoices(): Promise<Invoice[]> {
    const response = await this.api.get<Invoice[]>('/invoices');
    return response.data;
  }

  async getInvoicesByAccountId(contactID: string): Promise<Invoice[]> {
    const response = await this.api.get<Invoice[]>(`/invoices/account/${contactID}`);
    return response.data;
  }

  async createInvoice(invoice: Omit<Invoice, 'id'>): Promise<Invoice> {
    const response = await this.api.post<Invoice>('/invoices', invoice);
    return response.data;
  }

  async updateInvoice(invoiceID: string, invoice: Partial<Invoice>): Promise<Invoice | undefined> {
    try {
      const response = await this.api.put<Invoice>(`/invoices/${invoiceID}`, invoice);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async deleteInvoice(invoiceID: string): Promise<boolean> {
    try {
      await this.api.delete(`/invoices/${invoiceID}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/users');
    return response.data;
  }

  async getUserById(id: string): Promise<User | undefined> {
    try {
      const response = await this.api.get<User>(`/users/${id}`);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async createUser(user: Omit<User, 'id'>): Promise<User> {
    const response = await this.api.post<User>('/users', user);
    return response.data;
  }

  async updateUser(id: string, user: Partial<User>): Promise<User | undefined> {
    try {
      const response = await this.api.put<User>(`/users/${id}`, user);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async deleteUser(id: string): Promise<boolean> {
    try {
      await this.api.delete(`/users/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Changelog
  async getChangelog(): Promise<ChangelogEntry[]> {
    const response = await this.api.get<ChangelogEntry[]>('/changelog');
    return response.data;
  }

  async createChangelogEntry(entry: Omit<ChangelogEntry, 'id'>): Promise<ChangelogEntry> {
    const response = await this.api.post<ChangelogEntry>('/changelog', entry);
    return response.data;
  }

  // Credit Control Account Notes
  async getAccountNotes(accountID: string): Promise<AccountNote[]> {
    const response = await this.api.get<AccountNote[]>(`/credit-control/account-notes/account/${accountID}`);
    return response.data;
  }

  async addAccountNote(note: Omit<AccountNote, 'id' | 'noteId' | 'timestamp'>): Promise<AccountNote> {
    // Send only the required fields to match the CreateNoteRequest DTO
    const requestData = {
      accountID: note.accountID,
      content: note.content
    };
    const response = await this.api.post<AccountNote>('/credit-control/account-notes', requestData);
    return response.data;
  }

  async updateAccountNote(noteId: string, content: string): Promise<AccountNote | undefined> {
    try {
      const response = await this.api.put<AccountNote>(`/credit-control/account-notes/${noteId}`, { content });
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async deleteAccountNote(noteId: string): Promise<boolean> {
    try {
      await this.api.delete(`/credit-control/account-notes/${noteId}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Account Summary
  async getAccountSummary(): Promise<AccountSummary> {
    const response = await this.api.get<AccountSummary>('/credit-control/accounts/summary');
    return response.data;
  }

  // Update Account Overdue Status
  async updateAccountOverdueStatus(contactID: string): Promise<{ message: string; contactId: string }> {
    const response = await this.api.post<{ message: string; contactId: string }>('/credit-control/accounts/update-overdue-status', { contactID });
    return response.data;
  }

  // Manual Sync (Admin only)
  async manualSync(): Promise<SyncResult> {
    const response = await this.api.post<SyncResult>('/credit-control/accounts/manual-sync');
    return response.data;
  }

  // Debts - Overdue Invoices
  async getOverdueDebts(): Promise<any[]> {
    const response = await this.api.get('/debts');
    return response.data;
  }

  async getOverdueDebtsPaged(page: number = 1, pageSize: number = 20): Promise<any> {
    const response = await this.api.get(`/debts/paged?page=${page}&pageSize=${pageSize}`);
    return response.data;
  }

  // Health check
  async checkHealth(): Promise<{ status: string; version: string; timestamp: string }> {
    const response = await this.api.get<{ status: string; version: string; timestamp: string }>('/health');
    return response.data;
  }

  // Course Management - Courses
  async getCourses(): Promise<any[]> {
    const response = await this.api.get('/course');
    return response.data;
  }

  async getCourseById(id: number): Promise<any> {
    const response = await this.api.get(`/course/${id}`);
    return response.data;
  }

  async createCourse(course: any): Promise<any> {
    const response = await this.api.post('/course', course);
    return response.data;
  }

  async updateCourse(id: number, course: any): Promise<any> {
    const response = await this.api.put(`/course/${id}`, course);
    return response.data;
  }

  async deleteCourse(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/course/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Course Management - Locations
  async getLocations(): Promise<any[]> {
    const response = await this.api.get('/location');
    return response.data;
  }

  async getLocationById(id: number): Promise<any> {
    const response = await this.api.get(`/location/${id}`);
    return response.data;
  }

  async createLocation(location: any): Promise<any> {
    const response = await this.api.post('/location', location);
    return response.data;
  }

  async updateLocation(id: number, location: any): Promise<any> {
    const response = await this.api.put(`/location/${id}`, location);
    return response.data;
  }

  async deleteLocation(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/location/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Course Management - Sessions
  async getSessions(): Promise<any[]> {
    const response = await this.api.get('/session');
    return response.data;
  }

  async getSessionById(id: number): Promise<any> {
    const response = await this.api.get(`/session/${id}`);
    return response.data;
  }

  async createSession(session: any): Promise<any> {
    const response = await this.api.post('/session', session);
    return response.data;
  }

  async updateSession(id: number, session: any): Promise<any> {
    const response = await this.api.put(`/session/${id}`, session);
    return response.data;
  }

  async deleteSession(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/session/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Utility - Working Day Calculations
  async calculateWorkingDays(startDate: string, workingDays: number): Promise<{ endDate: string; skippedDates: string[] }> {
    const response = await this.api.post('/utility/calculate-working-days', {
      startDate,
      workingDays
    });
    return response.data;
  }

  async getBankHolidays(year: number): Promise<any[]> {
    const response = await this.api.get(`/utility/bank-holidays/${year}`);
    return response.data;
  }

  // Permission Management
  async getPermissions(): Promise<any[]> {
    const response = await this.api.get('/permission');
    return response.data;
  }

  async getPermissionsByCategory(): Promise<any[]> {
    const response = await this.api.get('/permission/categories');
    return response.data;
  }

  async getUserPermissions(userId: string): Promise<any[]> {
    const response = await this.api.get(`/permission/user/${userId}`);
    return response.data;
  }

  async updateUserPermissions(userId: string, permissionIds: number[]): Promise<any> {
    const response = await this.api.put(`/permission/user/${userId}`, { permissionIds });
    return response.data;
  }

  // Password reset functionality
  async resetPassword(userId: string): Promise<{ tempPassword: string; message: string }> {
    const response = await this.api.post(`/users/${userId}/reset-password`);
    return response.data;
  }

  async completeFirstLogin(userId: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.post(`/users/${userId}/complete-first-login`, { newPassword });
    return response.data;
  }

  // Credit Control Agreements
  async uploadAgreement(contactId: string, file: File, uploadedBy: string = ''): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadedBy', uploadedBy);

    const response = await this.api.post(`/credit-control/agreements/${contactId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getAgreement(contactId: string): Promise<any> {
    const response = await this.api.get(`/credit-control/agreements/${contactId}`);
    return response.data;
  }

  async downloadAgreement(contactId: string): Promise<Blob> {
    const response = await this.api.get(`/credit-control/agreements/${contactId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteAgreement(contactId: string): Promise<boolean> {
    try {
      await this.api.delete(`/credit-control/agreements/${contactId}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  async hasAgreement(contactId: string): Promise<boolean> {
    try {
      const response = await this.api.get(`/credit-control/agreements/${contactId}/exists`);
      return response.data;
    } catch (error) {
      return false;
    }
  }

  // Document Management
  async getDocuments(): Promise<any[]> {
    const response = await this.api.get('/documents');
    return response.data;
  }

  async getDocumentById(id: number): Promise<any> {
    const response = await this.api.get(`/documents/${id}`);
    return response.data;
  }

  async uploadDocument(request: { file: File; fileName?: string; sharedWithPages?: string[] }): Promise<any> {
    const formData = new FormData();
    formData.append('file', request.file);
    if (request.fileName) {
      formData.append('fileName', request.fileName);
    }
    if (request.sharedWithPages) {
      formData.append('sharedWithPages', request.sharedWithPages.join(','));
    }

    const response = await this.api.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateDocument(id: number, request: { file?: File; fileName?: string; sharedWithPages?: string[] }): Promise<any> {
    const formData = new FormData();
    if (request.file) {
      formData.append('file', request.file);
    }
    if (request.fileName) {
      formData.append('fileName', request.fileName);
    }
    if (request.sharedWithPages) {
      formData.append('sharedWithPages', request.sharedWithPages.join(','));
    }

    const response = await this.api.put(`/documents/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteDocument(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/documents/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  async downloadDocument(fileId: string): Promise<Blob> {
    const response = await this.api.get(`/documents/download/${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // PDF Templates
  async getPdfTemplates(): Promise<PdfTemplate[]> {
    const response = await this.api.get<PdfTemplate[]>('/pdftemplates');
    return response.data;
  }

  async getPdfTemplatesByType(templateType: string): Promise<PdfTemplate[]> {
    try {
      const response = await this.api.get<PdfTemplate[]>(`/pdftemplates/${templateType}`);
      return response.data;
    } catch (error) {
      return [];
    }
  }

  async getPdfTemplateByType(templateType: string): Promise<PdfTemplate | undefined> {
    try {
      const templates = await this.getPdfTemplatesByType(templateType);
      return templates.length > 0 ? templates[0] : undefined;
    } catch (error) {
      return undefined;
    }
  }

  async setPdfTemplate(request: SetTemplateRequest): Promise<PdfTemplate> {
    const response = await this.api.post<PdfTemplate>('/pdftemplates', request);
    return response.data;
  }

  async deletePdfTemplate(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/pdftemplates/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  async extractPdfFields(documentFileId: string): Promise<{ hasFields: boolean; fields: string[]; message: string }> {
    const response = await this.api.post<{ hasFields: boolean; fields: string[]; message: string }>('/pdftemplates/extract-fields', { documentFileId });
    return response.data;
  }

  async getUnmappedDocuments(): Promise<any[]> {
    const response = await this.api.get('/pdftemplates/unmapped-documents');
    return response.data;
  }

  // PDF Generation
  async generatePdf(request: {
    templateId: string;
    fieldMappings: Record<string, string>;
    filename?: string;
  }): Promise<Blob> {
    const response = await this.api.post('/pdftemplates/generate', request, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Email Templates
  async getEmailTemplates(): Promise<any[]> {
    const response = await this.api.get('/emailtemplates');
    return response.data;
  }

  async getEmailTemplateById(id: number): Promise<any> {
    const response = await this.api.get(`/emailtemplates/${id}`);
    return response.data;
  }

  async createEmailTemplate(request: { templateName: string; htmlFile: File; createdBy: string }): Promise<any> {
    const formData = new FormData();
    formData.append('templateName', request.templateName);
    formData.append('htmlFile', request.htmlFile);
    formData.append('createdBy', request.createdBy);

    const response = await this.api.post('/emailtemplates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateEmailTemplate(id: number, request: { templateName: string; htmlFile?: File; modifiedBy: string }): Promise<any> {
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('templateName', request.templateName);
    if (request.htmlFile) {
      formData.append('htmlFile', request.htmlFile);
    }
    formData.append('modifiedBy', request.modifiedBy);

    const response = await this.api.put(`/emailtemplates/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteEmailTemplate(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/emailtemplates/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  async sendEmail(request: {
    template: boolean;
    templateID: number;
    toEmail: string;
    fromEmail: string;
    subject: string;
    bodyElements: Record<string, string>;
    attachment?: File;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('template', request.template.toString());
    formData.append('templateID', request.templateID.toString());
    formData.append('toEmail', request.toEmail);
    formData.append('fromEmail', request.fromEmail);
    formData.append('email.subject', request.subject);
    
    // Add body elements
    Object.entries(request.bodyElements).forEach(([key, value]) => {
      formData.append(`email.bodyElements.${key}`, value);
    });

    if (request.attachment) {
      formData.append('attachment', request.attachment);
    }

    const response = await this.api.post('/emailtemplates/send', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async extractEmailFields(htmlContent: string): Promise<string[]> {
    const response = await this.api.post<string[]>('/emailtemplates/extract-fields', htmlContent, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  // Email Template Tags
  async getEmailTemplateTags(): Promise<any[]> {
    const response = await this.api.get('/emailtemplatetags');
    return response.data;
  }

  async assignTemplateToTag(tagName: string, templateId: string, force: boolean = false): Promise<any> {
    const endpoint = force ? `/emailtemplatetags/${tagName}/assign/force` : `/emailtemplatetags/${tagName}/assign`;
    const response = await this.api.post(endpoint, { templateId });
    return response.data;
  }

  async unassignTemplateFromTag(tagName: string): Promise<boolean> {
    try {
      await this.api.delete(`/emailtemplatetags/${tagName}/unassign`);
      return true;
    } catch (error) {
      return false;
    }
  }

  async getEmailTemplateTagStatus(tagName: string): Promise<any> {
    const response = await this.api.get(`/emailtemplatetags/${tagName}/status`);
    return response.data;
  }

  // Onboarding Management
  async getOnboardingUsersProgress(): Promise<OnboardingUserProgress[]> {
    const response = await this.api.get<OnboardingUserProgress[]>('/onboarding/users-progress');
    return response.data;
  }

  async getMyOnboardingSessions(): Promise<OnboardingSession[]> {
    const response = await this.api.get<OnboardingSession[]>('/onboarding/my-sessions');
    return response.data;
  }

  async getOnboardingUserSessions(userId: string): Promise<OnboardingSession[]> {
    const response = await this.api.get<OnboardingSession[]>(`/onboarding/sessions/${userId}`);
    return response.data;
  }

  async getOnboardingSessionById(id: number): Promise<OnboardingSession | undefined> {
    try {
      const response = await this.api.get<OnboardingSession>(`/onboarding/sessions/detail/${id}`);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async createOnboardingSession(session: OnboardingSessionCreateDto): Promise<OnboardingSession> {
    const response = await this.api.post<OnboardingSession>('/onboarding/sessions', session);
    return response.data;
  }

  async updateOnboardingSession(id: number, session: OnboardingSessionUpdateDto): Promise<OnboardingSession | undefined> {
    try {
      const response = await this.api.put<OnboardingSession>(`/onboarding/sessions/${id}`, session);
      return response.data;
    } catch (error) {
      return undefined;
    }
  }

  async deleteOnboardingSession(id: number): Promise<boolean> {
    try {
      await this.api.delete(`/onboarding/sessions/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  }

  // New onboarding management methods
  async getAvailableUsers(): Promise<any[]> {
    const response = await this.api.get('/onboarding/available-users');
    return response.data;
  }

  async addUserToOnboarding(userId: string): Promise<{ message: string }> {
    const response = await this.api.post('/onboarding/add-user', { userId });
    return response.data;
  }

  async createSessionsForUser(userId: string, sessionTemplates: any[]): Promise<{ message: string; sessions: OnboardingSession[] }> {
    const response = await this.api.post('/onboarding/create-sessions', {
      userId,
      sessionTemplates
    });
    return response.data;
  }

  async getSessionTemplates(): Promise<any[]> {
    const response = await this.api.get('/onboarding/session-templates');
    return response.data;
  }

  // Template-based onboarding methods
  async getOnboardingTemplates(): Promise<OnboardingTemplate[]> {
    const response = await this.api.get<OnboardingTemplate[]>('/onboarding/templates');
    return response.data;
  }

  async addUserWithTemplate(request: AddUserWithTemplateRequest): Promise<{ message: string; sessions: OnboardingSession[] }> {
    const response = await this.api.post('/onboarding/add-user-with-template', request);
    return response.data;
  }

  async previewTemplateSchedule(request: PreviewTemplateScheduleRequest): Promise<PreviewSession[]> {
    const response = await this.api.post<PreviewSession[]>('/onboarding/preview-template-schedule', request);
    return response.data;
  }

  async previewDatabaseInsert(request: PreviewDatabaseInsertRequest): Promise<DatabasePreviewSession[]> {
    const response = await this.api.post<DatabasePreviewSession[]>('/onboarding/preview-database-insert', request);
    return response.data;
  }

  // Tutor Sessions
  async getTutorSessions(): Promise<any[]> {
    const response = await this.api.get('/tutorsession');
    return response.data;
  }

  // Exam Link
  async getExamLink(courseGuid: string): Promise<{ 
    success: boolean; 
    examLink: string; 
    reSitExamLink: string;
    errorMessage: string;
    examLinkError: string;
    reSitExamLinkError: string;
    hasExamLink: boolean;
    hasReSitExamLink: boolean;
  }> {
    try {
      const response = await this.api.get(`/tutorsession/${courseGuid}/examlink`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Clear invalid token and return authentication error
        this.clearToken();
        return {
          success: false,
          examLink: '',
          reSitExamLink: '',
          errorMessage: 'Authentication failed. Please log in again to access exam links.',
          examLinkError: '',
          reSitExamLinkError: '',
          hasExamLink: false,
          hasReSitExamLink: false
        };
      }
      // For other errors, return generic error
      return {
        success: false,
        examLink: '',
        reSitExamLink: '',
        errorMessage: 'Failed to fetch exam links. Please contact Course Ops for assistance.',
        examLinkError: '',
        reSitExamLinkError: '',
        hasExamLink: false,
        hasReSitExamLink: false
      };
    }
  }

}

export const apiService = new ApiService();
